/**
 * Presentation 2 - schema definition
 * ============================================================================
 *
 * - Collection
 * - Manifest
 * - Sequence
 * - Canvas
 * - Annotation
 * - AnnotationList
 * - Range
 * - Layer
 * - Image Content (Annotation)
 * - Other Content (Annotation List)
 *
 * - External resources
 * - Services
 */

// @todo Image Service API.
import { compose } from 'redux';
import { schema, normalize } from 'normalizr';
import moveStartCanvasToSequence from '../compat/moveStartCanvasToSequence';
import preprocessLinkedEntities from '../compat/preprocessLinkedEntities';
import addMissingIds from '../compat/addMissingIds';

function createEntity(name) {
  var options = {
    idAttribute: '@id'
  };

  return new schema.Entity(name, {}, options);
}

var collection = createEntity('collections');
var manifest = createEntity('manifests');
var sequence = createEntity('sequences');
var canvas = createEntity('canvases');
var annotation = createEntity('annotations');
var annotationList = createEntity('annotationLists');
var range = createEntity('ranges');
var layer = createEntity('layers');
var imageResource = createEntity('imageResources'); // thumbnails + image service
var choice = createEntity('choices');

// Unofficial types?
var externalResource = createEntity('externalResources');
var service = createEntity('services', true);
var within = new schema.Array({
  collection: collection,
  manifest: manifest,
  externalResource: externalResource,
  layer: layer
}, function (entity) {
  switch (entity['@type']) {
    case 'sc:Collection':
      return 'collection';
    case 'sc:Manifest':
      return 'manifest';
    case 'sc:Layer':
      return 'layer';
    default:
      return 'externalResource';
  }
});

var rangeMember = new schema.Union({
  canvas: canvas,
  range: range
}, function (input) {
  return input['@type'] === 'sc:Canvas' ? 'canvas' : 'range';
});

/**
 * Dereferencable resources:
 *
 * - Collection (Required)
 * - Manifest (Required)
 * - First Sequence (Optional)
 * - Nth Sequence (Required)
 * - Canvas (Optional)
 * - Annotation (Recommended)
 * - AnnotationList (Required)
 * - Range (Optional)
 * - Layer (Optional)
 * - Image Content (Required)
 * - Other Content (Required)
 */
var RESOURCE_TYPE_MAP = {
  'sc:Collection': 'collection',
  'sc:Sequence': 'sequence',
  'sc:Manifest': 'manifest',
  'sc:Canvas': 'canvas',
  'sc:AnnotationList': 'annotationList',
  'sc:Annotation': 'annotation',
  'sc:Range': 'range',
  'sc:Layer': 'layer',
  'dctypes:Image': 'imageResource'
};

var SCHEMAS = {
  collection: collection,
  sequence: sequence,
  manifest: manifest,
  canvas: canvas,
  annotationList: annotationList,
  annotation: annotation,
  range: range,
  layer: layer,
  imageResource: imageResource,
  service: service
};

var resource = new schema.Union({
  collection: collection,
  sequence: sequence,
  manifest: manifest,
  canvas: canvas,
  annotationList: annotationList,
  annotation: annotation,
  range: range,
  layer: layer,
  imageResource: imageResource,
  service: service
}, function (entity) {
  if (RESOURCE_TYPE_MAP[entity['@type']]) {
    return RESOURCE_TYPE_MAP[entity['@type']];
  }
  if (entity.profile) {
    return 'service';
  }
  throw Error('Unknown entity type');
});

// ===========================================================================
// 1) Collections
// ===========================================================================
collection.define({
  collections: new schema.Array({
    collection: collection,
    manifest: manifest
  }, function (entity) {
    return entity['@type'] === 'sc:Manifest' ? 'manifest' : 'collection';
  }),
  manifests: [manifest],
  members: new schema.Array({
    collection: collection,
    manifest: manifest
  }, function (entity) {
    return entity['@type'] === 'sc:Manifest' ? 'manifest' : 'collection';
  }),

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  otherContent: [annotationList],
  logo: [imageResource],
  within: within,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 2) Manifests
// ===========================================================================
manifest.define({
  sequences: [sequence],
  structures: [range],

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  otherContent: [annotationList],
  logo: [imageResource],
  within: within,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 3) Sequences
// ===========================================================================
sequence.define({
  canvases: [canvas],

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  otherContent: [annotationList],
  logo: [imageResource],
  within: within,
  startCanvas: canvas,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 4) Canvases
// ===========================================================================
canvas.define({
  images: [annotation],

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  otherContent: [annotationList],
  logo: [imageResource],
  within: within,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 5) Annotation
// ===========================================================================
annotation.define({
  resource: new schema.Union({
    imageResource: imageResource,
    externalResource: externalResource,
    choice: choice
  }, function (input) {
    switch (input['@type']) {
      case 'dctypes:Image':
        return 'imageResource';
      case 'oa:Choice':
        return 'choice';
      default:
        return 'externalResource';
    }
  }),
  on: canvas,

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  logo: [imageResource],
  within: within,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 6) Annotation List
// ===========================================================================
annotationList.define({
  resources: [annotation],

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  logo: [imageResource],
  within: within,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 7) Range
// ===========================================================================
range.define({
  resources: [annotation],
  members: [rangeMember],
  canvases: [canvas],
  ranges: [range],

  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  logo: [imageResource],
  within: within,
  startCanvas: canvas,
  contentLayer: layer,

  // Extra
  thumbnail: imageResource
});

// ===========================================================================
// 8) Layer
// ===========================================================================
layer.define({
  // Linking
  seeAlso: [externalResource],
  service: [service],
  related: [externalResource],
  rendering: [externalResource],
  logo: [imageResource],
  within: within
});

// ===========================================================================
// 9) Image resource
// ===========================================================================
imageResource.define({
  service: [service]
});

// ===========================================================================
// 10) Choice
// ===========================================================================
choice.define({
  default: annotation,
  item: [annotation]
});

var preprocess = compose(moveStartCanvasToSequence, preprocessLinkedEntities, addMissingIds(2.1));

var normalizeResource = function normalizeResource(rawResource) {
  var customSchema = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : resource;
  return normalize(preprocess(rawResource), customSchema);
};

export { RESOURCE_TYPE_MAP, SCHEMAS, normalizeResource as normalize, preprocess, resource, collection, manifest, sequence, canvas, annotation, annotationList, range, layer, imageResource, externalResource, within, service };